import React from 'react';
import { Flex, Tooltip, Typography } from 'antd';

import { useUIColors } from '../../theme';

const { Title, Text } = Typography;

export interface PageHeaderProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    breadcrumb?: React.ReactNode;
    level?: 1 | 2 | 3 | 4;
    /** Bottom spacing under the header block. Fit-to-shell pages pass `0` and
     *  rely on the page column `gap` instead (see accounts frame 69). */
    marginBottom?: number;
    /**
     * Collapse `subtitle` into an (i) beside the title instead of printing it.
     *
     * Default `true` (EAA-1846, design frame 95 "Vỏ chuẩn"): the title strip's
     * description is MANDATORY tooltip-only ("Mô tả không in ra dải tiêu đề"),
     * not opt-in — a page that still wants the old printed line (a genuinely
     * per-visit fact like a live count) sets `collapseSubtitle={false}`
     * explicitly, which is now the exception, not the default.
     */
    collapseSubtitle?: boolean;
}

/**
 * Tier-1 presentational primitive: a page-level title block with an optional
 * breadcrumb line, subtitle, and a right-aligned actions slot. antd v5+
 * removed its own `PageHeader` component, so this is built from `Flex` and
 * `Typography` primitives instead of relying on that (now-gone) API.
 */
export function PageHeader({
    title,
    subtitle,
    actions,
    breadcrumb,
    level = 2,
    collapseSubtitle = true,
    marginBottom = 24,
}: PageHeaderProps): React.ReactElement {
    const colors = useUIColors();
    const collapsed = collapseSubtitle && Boolean(subtitle);

    return (
        <div className="page-header" style={{ marginBottom }}>
            {breadcrumb && <div style={{ marginBottom: 8 }}>{breadcrumb}</div>}

            <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
                <div style={{ minWidth: 0 }}>
                    <Flex align="center" gap={8}>
                        <Title level={level} style={{ margin: 0 }}>
                            {title}
                        </Title>
                        {collapsed && (
                            <Tooltip
                                title={subtitle}
                                color="#0d3b47"
                                overlayInnerStyle={{
                                    color: '#eaf6f9',
                                    fontSize: 12.5,
                                    lineHeight: 1.5,
                                    width: 340,
                                }}
                            >
                                <button
                                    type="button"
                                    aria-label="Giới thiệu về trang này"
                                    style={{
                                        display: 'inline-flex',
                                        flex: 'none',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 22,
                                        height: 22,
                                        padding: 0,
                                        border: 0,
                                        borderRadius: 999,
                                        background: '#007fa3',
                                        color: '#fff',
                                        fontSize: 12,
                                        fontWeight: 800,
                                        cursor: 'help',
                                    }}
                                >
                                    i
                                </button>
                            </Tooltip>
                        )}
                    </Flex>
                    {subtitle && !collapsed && (
                        <Text type="secondary" style={{ color: colors.textMuted }}>
                            {subtitle}
                        </Text>
                    )}
                </div>

                {actions && (
                    <Flex align="center" gap={8} wrap="wrap" style={{ flexShrink: 0 }}>
                        {actions}
                    </Flex>
                )}
            </Flex>
        </div>
    );
}

export default PageHeader;
